import { useRef, useState, RefObject } from 'react';

import { debounce } from '../utils';

/**
 * @example const [ref, resizeFunction] = useAutoResize();
 * maxHeight should be provided in pixels without units
 * minHeight should be provided in pixels without units
 * @returns * ref:: A reference for the element that the resize functionality should be applied to. [React RefObject](https://reactjs.org/docs/refs-and-the-dom.html)
 *          * resizeFunction:: The function to call to automatically resize the element.
 * element refers to auto resizable element
 */
const useAutoResize = <T extends HTMLElement = HTMLElement>(
  maxHeight?: number,
  minHeightProp?: number
): [RefObject<T>, () => void] => {
  const element = useRef<T>(null);

  const [minHeight, setMinHeight] = useState(minHeightProp || 0);

  const autoResize = () => {
    if (element.current) {
      let newMinHeight = minHeight;
      if (!minHeight) {
        newMinHeight = element.current.scrollHeight;
        setMinHeight(newMinHeight);
      }
      // Reset the heights to the base height so there is an overflow / or none to calculate the new height
      element.current.style.height = `${newMinHeight}px`;

      if (maxHeight) {
        element.current.style.overflowY =
          element.current.scrollHeight > maxHeight ? 'auto' : 'hidden';
        element.current.style.height = `${Math.min(element.current.scrollHeight, maxHeight)}px`;
      } else {
        element.current.style.height = `${element.current.scrollHeight}px`;
      }
    }
  };

  const debouncedAutoResize = debounce(autoResize, 100);

  return [element, debouncedAutoResize];
};

export default useAutoResize;
